// Wraps window.fetch. The page always gets the original promise back, untouched;
// we read a clone of the response on a side chain.

import {
  absoluteUrl,
  bodySummary,
  graphqlErrors,
  headersToObject,
  isTextual,
  readLimited,
  shortUrl,
} from '../net-utils.js';

function describeRequest(input, init) {
  const isRequest = typeof Request !== 'undefined' && input instanceof Request;
  const rawUrl = isRequest ? input.url : input instanceof URL ? input.href : String(input);
  const method = String((init && init.method) || (isRequest ? input.method : 'GET')).toUpperCase();
  const headers = headersToObject((init && init.headers) || (isRequest ? input.headers : undefined));
  return { isRequest, url: absoluteUrl(rawUrl), method, headers };
}

function readableBody(response) {
  if (response.type === 'opaque' || response.type === 'opaqueredirect' || !response.body) return false;
  const contentType = response.headers.get('content-type') || '';
  return isTextual(contentType) && !/text\/event-stream/i.test(contentType);
}

function levelFor(status) {
  if (status >= 500) return 'error';
  if (status >= 400) return 'warn';
  return 'info';
}

export function installFetch(ctx, win = window) {
  const original = win.fetch;
  if (typeof original !== 'function') return;
  ctx.originals.fetch = original.bind(win);
  const max = ctx.config.maxBodyLength;

  const wrapped = function fetch(input, init) {
    let info = null;
    try {
      info = describeRequest(input, init);
    } catch {
      info = null;
    }
    // init.__adidoBugs marks our own requests (for example screenshot image fetches).
    // It is not a real fetch option, so the browser ignores it.
    const ours = init && init.__adidoBugs;
    if (!info || ours || ctx.busy || ctx.isOwnUrl(info.url)) return original.apply(this, arguments);

    const startedAt = Date.now();
    const t0 = performance.now();
    const safeUrl = ctx.redact.url(info.url);

    // Request body: init.body is available now; a Request object's body has to be read from a clone.
    let requestBody;
    if (init && init.body !== undefined) {
      requestBody = Promise.resolve(bodySummary(init.body, max));
    } else if (info.isRequest && input.body && !input.bodyUsed && !['GET', 'HEAD'].includes(info.method)) {
      try {
        requestBody = readLimited(input.clone().body, max).then(({ text, truncated }) => ({
          body: text,
          truncated: truncated || undefined,
        }));
      } catch {
        requestBody = Promise.resolve({ note: '[request body not readable]' });
      }
    } else {
      requestBody = Promise.resolve({});
    }

    const promise = original.apply(this, arguments);

    promise.then(
      (response) => {
        // Clone only bodies we will read. An unread clone of a stream (a download, an
        // event stream) would buffer everything the page reads, with no limit.
        let clone = null;
        if (readableBody(response)) {
          try {
            clone = response.clone();
          } catch {
            clone = null;
          }
        }
        record(response, clone).catch(() => {});
      },
      (error) => {
        recordFailure(error).catch(() => {});
      },
    );
    return promise;

    async function baseDetail() {
      const request = await requestBody.catch(() => ({}));
      return {
        url: ctx.redact.url(info.url),
        method: info.method,
        initiator: 'fetch',
        startedAt,
        duration: Math.round(performance.now() - t0),
        request: {
          headers: ctx.redact.headers(info.headers),
          ...ctx.redact.summary(request),
        },
      };
    }

    async function record(response, clone) {
      const detail = await baseDetail();
      const contentType = response.headers.get('content-type') || '';
      const responsePart = {
        headers: ctx.redact.headers(headersToObject(response.headers)),
        contentType: contentType || undefined,
        size: Number(response.headers.get('content-length')) || undefined,
      };
      if (response.type === 'opaque' || response.type === 'opaqueredirect') {
        responsePart.note = 'Opaque response (no-cors): status and body are hidden from page scripts';
      } else if (/text\/event-stream/i.test(contentType)) {
        responsePart.note = '[event stream not captured]';
      } else if (!isTextual(contentType)) {
        responsePart.note = `[${contentType} body not captured]`;
      } else if (clone && clone.body) {
        const { text, truncated, error } = await readLimited(clone.body, max);
        responsePart.body = ctx.redact.body(text);
        if (truncated) responsePart.truncated = true;
        if (error) responsePart.note = `Body read failed: ${error}`;
      }
      // Real duration is up to headers; body download time is included when we read the body.
      detail.duration = Math.round(performance.now() - t0);
      Object.assign(detail, {
        status: response.status,
        statusText: response.statusText || undefined,
        ok: response.ok,
        type: response.type,
        redirected: response.redirected || undefined,
        finalUrl: response.redirected ? ctx.redact.url(response.url) : undefined,
        response: responsePart,
      });
      const gql = response.ok ? graphqlErrors(responsePart.body) : null;
      if (gql) detail.graphqlErrors = gql;

      const clean = ctx.sanitise('network', detail);
      if (!clean) return;
      const status = response.type === 'opaque' ? 'opaque' : response.status;
      const level = gql ? 'warn' : levelFor(response.status);
      const suffix = gql ? ' (GraphQL errors)' : '';
      ctx.push('network', level, `${info.method} ${shortUrl(safeUrl)} ${status}${suffix}`, clean, startedAt);
    }

    async function recordFailure(error) {
      const detail = await baseDetail();
      const aborted = error && error.name === 'AbortError';
      Object.assign(detail, {
        status: 0,
        failed: true,
        aborted: aborted || undefined,
        error: String((error && error.message) || error),
      });
      const clean = ctx.sanitise('network', detail);
      if (!clean) return;
      ctx.push(
        'network',
        aborted ? 'warn' : 'error',
        `${info.method} ${shortUrl(safeUrl)} ${aborted ? 'aborted' : 'failed'}`,
        clean,
        startedAt,
      );
    }
  };

  win.fetch = wrapped;
}
